import { apiClient } from '@/shared/services/api/client';
import { unwrapEnvelope } from '@/shared/services/api/envelope';
import type { ApiEnvelope } from '@/shared/services/api/envelope';
import type { RfqCustomFieldValue } from '@/features/bids';
import type { ColumnSort } from '../utils/supplierQuotation';
import type {
  SupplierBidDocument,
  SupplierBidOwnDocument,
  SupplierBidRow,
  SupplierBidNoQuote,
  SupplierBidValueMode,
  SupplierBidView,
} from '../models/supplierBid.types';

/**
 * The only module that knows the supplier's endpoints (`/api/v1/supplier-bids`, backend `modules/quotation`): the
 * BIDs a supplier is invited to, and its quotation on one of them.
 *
 * Moved out of the BIDs feature's `rfqService` (2026-10-28) with the rest of the Supplier Bid Quotation. Like it, there
 * is no mock branch: tests replace this module with `vi.mock`.
 */

const SUPPLIER_BIDS = '/supplier-bids';
const FALLBACK = 'The BID request could not be completed. Please try again.';

/*
  Background reads opt out of the global 403 -> /unauthorized navigation: a refused read is the screen's news to
  report in place, not grounds to throw the supplier out of its portal.
*/
const allowForbidden = { _allowForbidden: true } as const;

/** A downloaded file: its bytes and the name to save it under. */
export interface DownloadedFile {
  blob: Blob;
  fileName: string;
}

async function getData<T>(url: string): Promise<T> {
  return unwrapEnvelope(await apiClient.get<ApiEnvelope<T>>(url, allowForbidden), FALLBACK);
}

async function postData<T>(url: string, body?: unknown): Promise<T> {
  return unwrapEnvelope(await apiClient.post<ApiEnvelope<T>>(url, body ?? {}), FALLBACK);
}

async function putData<T>(url: string, body: unknown): Promise<T> {
  return unwrapEnvelope(await apiClient.put<ApiEnvelope<T>>(url, body), FALLBACK);
}

async function deleteData<T>(url: string): Promise<T> {
  return unwrapEnvelope(await apiClient.delete<ApiEnvelope<T>>(url), FALLBACK);
}

/** A file name from `Content-Disposition`, or the fallback. */
function fileNameFrom(disposition: unknown, fallback: string): string {
  if (typeof disposition !== 'string') return fallback;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (star?.[1]) return decodeURIComponent(star[1]);
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1] ?? fallback;
}

async function download(url: string, fallbackName: string): Promise<DownloadedFile> {
  const response = await apiClient.get<Blob>(url, { responseType: 'blob' });
  return { blob: response.data, fileName: fileNameFrom(response.headers['content-disposition'], fallbackName) };
}

const one = (rfqId: number) => `${SUPPLIER_BIDS}/${String(rfqId)}`;

export const supplierQuotationService = {
  /**
   * The published BIDs the caller's e-mail is invited to, open ones first -- or sorted by a column on the server
   * (2026-10-29), Spring's `sort=field,dir` as the Master lists send it. Sent only when a column is chosen.
   */
  async supplierBids(sort?: ColumnSort | null): Promise<SupplierBidRow[]> {
    return unwrapEnvelope(
      await apiClient.get<ApiEnvelope<SupplierBidRow[]>>(SUPPLIER_BIDS, {
        ...allowForbidden,
        ...(sort ? { params: { sort: `${sort.key},${sort.dir}` } } : {}),
      }),
      FALLBACK,
    );
  },

  /** One invited BID with the template's Supplier Bid Form and the caller's quotation. */
  supplierBid(rfqId: number): Promise<SupplierBidView> {
    return getData<SupplierBidView>(one(rfqId));
  },

  saveSupplierBid(
    rfqId: number,
    values: RfqCustomFieldValue[],
    /* How each product's Discount and Freight / P&F read (2026-10-26); left out, the platform keeps what it has. */
    modes?: SupplierBidValueMode[],
    /* The products the supplier cannot supply (2026-10-26), all of them; left out, the platform keeps what it has. */
    noQuote?: SupplierBidNoQuote[],
  ): Promise<SupplierBidView> {
    return putData<SupplierBidView>(one(rfqId), { values, modes, noQuote });
  },

  /** Saves the answers and submits; the platform names every mandatory answer still missing. */
  submitSupplierBid(
    rfqId: number,
    values: RfqCustomFieldValue[],
    modes?: SupplierBidValueMode[],
    noQuote?: SupplierBidNoQuote[],
  ): Promise<SupplierBidView> {
    return postData<SupplierBidView>(`${one(rfqId)}/submit`, { values, modes, noQuote });
  },

  /** One of the BID's documents (BID-level or a product's), as the supplier downloads it. */
  downloadSupplierBidDocument(rfqId: number, doc: SupplierBidDocument): Promise<DownloadedFile> {
    return download(`${one(rfqId)}/documents/${String(doc.id)}`, doc.fileName);
  },

  /** Withdraws a quotation not yet submitted. */
  declineSupplierBid(rfqId: number, reason: string): Promise<SupplierBidView> {
    return postData<SupplierBidView>(`${one(rfqId)}/decline`, { reason });
  },

  /** Accepts the invitation (Intent to Participate): opens the draft quotation. */
  acceptSupplierBid(rfqId: number): Promise<SupplierBidView> {
    return postData<SupplierBidView>(`${one(rfqId)}/accept`);
  },

  /**
   * Revise Quotation (2026-10-30): after a deadline extension, the submitted quotation's next version -- a draft copied
   * from it -- to edit and submit; the submitted version is kept as it was. An open revision is returned as it stands.
   */
  reviseSupplierBid(rfqId: number): Promise<SupplierBidView> {
    return postData<SupplierBidView>(`${one(rfqId)}/revise`);
  },

  /** Withdraws a submitted quotation before the deadline; the supplier cannot quote on the BID again. */
  withdrawSupplierBid(rfqId: number, reason: string): Promise<SupplierBidView> {
    return postData<SupplierBidView>(`${one(rfqId)}/withdraw`, { reason });
  },

  /** Uploads one of the supplier's documents to a file field of the quotation (per product where it is). */
  async uploadSupplierBidFile(
    rfqId: number,
    fieldId: number,
    lineId: number | null,
    file: File,
  ): Promise<SupplierBidView> {
    const form = new FormData();
    form.append('file', file);
    form.append('fieldId', String(fieldId));
    if (lineId !== null) form.append('lineId', String(lineId));
    return unwrapEnvelope(
      await apiClient.post<ApiEnvelope<SupplierBidView>>(`${one(rfqId)}/files`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
      FALLBACK,
    );
  },

  deleteSupplierBidFile(rfqId: number, fileId: number): Promise<SupplierBidView> {
    return deleteData<SupplierBidView>(`${one(rfqId)}/files/${String(fileId)}`);
  },

  downloadSupplierBidFile(rfqId: number, doc: SupplierBidOwnDocument): Promise<DownloadedFile> {
    return download(`${one(rfqId)}/files/${String(doc.id)}`, doc.fileName);
  },

  /** The server's Quotation Sheet: a CSV of the BID's products with the per-product quotation fields. */
  downloadQuotationSheet(rfqId: number, rfqNumber: string): Promise<DownloadedFile> {
    return download(`${one(rfqId)}/quotation-sheet`, `${rfqNumber}_Quotation_Sheet.csv`);
  },

  /** Reads a filled CSV Quotation Sheet into the draft quotation; nothing is submitted. */
  async importQuotationSheet(rfqId: number, file: File): Promise<SupplierBidView> {
    const form = new FormData();
    form.append('file', file);
    return unwrapEnvelope(
      await apiClient.post<ApiEnvelope<SupplierBidView>>(`${one(rfqId)}/quotation-sheet`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
      FALLBACK,
    );
  },
};

export type SupplierQuotationService = typeof supplierQuotationService;
